import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Menu, Popover } from '@headlessui/react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
} from '@heroicons/react/20/solid';

import { useTranslation } from 'react-i18next';
import {
  PiUsersLight,
  PiMapPinLight,
  PiTagLight,
  PiPlusLight,
  PiCalendarBlankLight,
  PiFunnelLight,
  PiPaletteLight,
  PiDotsThreeVerticalLight,
} from 'react-icons/pi';
import Header, { Action } from '../shared/TailwindComponents/Header';
import Button from '../shared/TailwindComponents/Button';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import GlassCard from '../shared/brand/GlassCard';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { EVENT_TYPE_COLORS, CalendarEventType } from '../Calendar/types';

dayjs.extend(isBetween);
dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

/** Parse backend ISO timestamp as UTC and return as local dayjs.
 *  Backend sometimes omits the Z/offset suffix; without explicit UTC parsing
 *  dayjs treats it as local and dates shift. */
function parseEventDate(timestamp: any): dayjs.Dayjs {
  if (!timestamp) return dayjs();
  const trimmed = String(timestamp).trim();
  const hasTz = /Z$/i.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed);
  return hasTz ? dayjs(trimmed) : dayjs.utc(trimmed).local();
}

/** Get Tailwind color classes for a given event_type, falling back to blue */
function getEventColors(eventType?: string) {
  const key = (eventType || 'custom') as CalendarEventType;
  return EVENT_TYPE_COLORS[key] ?? EVENT_TYPE_COLORS.custom;
}

interface CalendarViewProps {
  tasks: any[];
  onRangeChange: (start: Date, end: Date) => void;
  onSelectEvent: (event: any) => void;
  onEventDrop?: (eventId: string, newDeadline: Date) => void;
  calendarViewMode: 'day' | 'week' | 'month' | 'year';
  onCalendarViewModeChange: (mode: 'day' | 'week' | 'month' | 'year') => void;
  onAddTask?: () => void;
  currentDate: dayjs.Dayjs;
  onDateChange: (newDate: dayjs.Dayjs) => void;
  isDayLoading?: boolean;
  /**
   * The occasional tools (Sync, Export, Import, Subscribe). They live in the
   * toolbar's overflow menu rather than as four more buttons in the row
   * (cal-1): nine controls above the grid is not a toolbar, it is a form.
   */
  overflowActions?: Action[];
  /** Filters button state — the panel itself is `filterBar`, owned by the page. */
  filterControl?: { open: boolean; activeCount: number; onToggle: () => void };
  /** The event-type key, shown inside a popover behind the palette icon. */
  legend?: React.ReactNode;
  /** Rendered under the toolbar; the page decides whether it is open. */
  filterBar?: React.ReactNode;
}

function classNames(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

/** The chip chrome every event block shares — the tint itself comes from the
 *  event type's token style, so a chip is identical in month, week and day. */
const EVENT_CHIP = 'rounded-md ring-1 ring-inset transition-all duration-100 ease-pop hover:shadow-sm';

const EventTooltip = ({ event }: { event: any }) => {
  const colors = getEventColors(event.event_type);
  return (
  <div className="w-80 px-5 py-4 glass shadow-xl rounded-xl transition-all duration-150 ease-pop">
    {/* Header */}
    <div className="flex items-start gap-3 mb-3">
      <div className={`w-8 h-8 rounded-lg ${colors.chip} ring-1 ring-inset ${colors.ring} flex items-center justify-center flex-shrink-0`}>
        <ClockIcon className={`h-4 w-4 ${colors.text}`} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold text-fg-strong truncate text-sm leading-tight">{event.title}</h3>
        {event.start_time && (
          <p className="text-xs text-fg-muted mt-0.5">
            {parseEventDate(event.start_time).format('h:mm A, ddd MMM D')}
          </p>
        )}
      </div>
    </div>

    {/* Description */}
    {event.description && (
      <p className="text-xs text-fg-body line-clamp-3 leading-relaxed mb-3">{event.description}</p>
    )}

    {/* Location */}
    {event.location && (
      <div className="flex items-center gap-2 text-xs text-fg-muted mb-3">
        <PiMapPinLight className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate">{event.location}</span>
      </div>
    )}

    {/* Shared with */}
    {(event.shared_with?.length || 0) > 0 && (
      <div className="pt-3 border-t border-hairline-soft">
        <div className="flex items-center gap-1.5 mb-1.5">
          <PiUsersLight className="h-3.5 w-3.5 text-fg-muted" />
          <span className="text-[10px] font-medium uppercase tracking-wider text-fg-muted">{event.shared_with.length} shared</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {event.shared_with.slice(0, 5).map((collab: any, i: number) => (
            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] bg-surface-sunken/70 border border-hairline-soft text-fg-body truncate max-w-[120px]">
              {collab.Name}
            </span>
          ))}
          {event.shared_with.length > 5 && (
            <span className="text-[10px] text-fg-muted">+{event.shared_with.length - 5}</span>
          )}
        </div>
      </div>
    )}

    {/* Tags */}
    {(event.tags?.length || 0) > 0 && (
      <div className="mt-3 pt-3 border-t border-hairline-soft flex flex-wrap gap-1">
        {event.tags.map((tag: string) => (
          <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-champagne/10 text-fg-body ring-1 ring-inset ring-champagne/30">
            <PiTagLight className="h-2.5 w-2.5 mr-0.5 flex-shrink-0" />
            {tag}
          </span>
        ))}
      </div>
    )}
  </div>
  );
};

const EventItem = ({ event, onSelect }: { event: any; onSelect: (event: any) => void }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const itemRef = React.useRef<HTMLLIElement>(null);
  const [tooltipStyle, setTooltipStyle] = React.useState<React.CSSProperties>({});

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsHovered(true);

    // Calculate tooltip position to avoid viewport clipping
    if (itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      const tooltipWidth = 320;
      const tooltipHeight = 250;
      const padding = 12;

      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      let top = rect.bottom + 8;

      // Clamp horizontal
      if (left < padding) left = padding;
      if (left + tooltipWidth > window.innerWidth - padding) left = window.innerWidth - padding - tooltipWidth;

      // Flip to above if below viewport
      if (top + tooltipHeight > window.innerHeight - padding) {
        top = rect.top - tooltipHeight - 8;
      }

      setTooltipStyle({ position: 'fixed', left, top, zIndex: 9999 });
    }
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsHovered(false), 300);
  };

  return (
    <li ref={itemRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        onClick={() => onSelect(event)}
        className="flex w-full items-center text-left text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40 group hover:bg-surface-sunken/70 rounded-md px-1 py-0.5 transition-colors duration-100"
      >
        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 flex-shrink-0 ${getEventColors(event.event_type).dot}`} />
        <p className="flex-auto truncate font-medium text-fg-body group-hover:text-fg-strong">
          {event.title}
        </p>
        <time
          dateTime={event.start_time || ''}
          className="ml-3 hidden flex-none text-fg-muted group-hover:text-fg-body xl:block"
        >
          {event.start_time ? parseEventDate(event.start_time).format('hA') : ''}
        </time>
      </button>
      {isHovered && ReactDOM.createPortal(
        <div style={tooltipStyle}>
          <EventTooltip event={event} />
        </div>,
        document.body
      )}
    </li>
  );
};

const CalendarView: React.FC<CalendarViewProps> = ({
  calendarViewMode,
  onCalendarViewModeChange,
  tasks,
  onRangeChange,
  onSelectEvent,
  onEventDrop,
  onAddTask,
  onDateChange,
  currentDate,
  isDayLoading = false,
  overflowActions,
  filterControl,
  legend,
  filterBar,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const containerNavRef = useRef<HTMLDivElement | null>(null);
  const containerOffsetRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation()
  const { setBreadcrumbs } = useBreadcrumbs();

  const updateBreadcrumbs = () => {
    const viewDetail = calendarViewMode === 'day' ? currentDate.format('MMMM D, YYYY') :
      calendarViewMode === 'week' ? `${currentDate.startOf('week').format('MMM D')} - ${currentDate.endOf('week').format('MMM D, YYYY')}` :
        calendarViewMode === 'month' ? currentDate.format('MMMM YYYY') :
          currentDate.format('YYYY');

    setBreadcrumbs([
      { name: t('Calendar'), href: '/calendar', current: false },
      { name: viewDetail, href: '#', current: true },
    ]);
  };

  useEffect(() => {
    updateBreadcrumbs();
  }, [calendarViewMode, currentDate]);

  useEffect(() => {
    updateBreadcrumbs();
  }, []);

  const getRange = useCallback(() => {
    let start: dayjs.Dayjs;
    let end: dayjs.Dayjs;

    if (calendarViewMode === 'month') {
      start = currentDate.startOf('month').startOf('week');
      end = currentDate.endOf('month').endOf('week');
    } else if (calendarViewMode === 'week') {
      start = currentDate.startOf('week');
      end = currentDate.endOf('week');
    } else if (calendarViewMode === 'day') {
      start = currentDate.startOf('day');
      end = currentDate.endOf('day');
    } else {
      start = currentDate.startOf('year');
      end = currentDate.endOf('year');
    }
    return { start, end };
  }, [calendarViewMode, currentDate]);

  useEffect(() => {
    const { start, end } = getRange();
    onRangeChange(start.toDate(), end.toDate());
  }, [getRange, onRangeChange]);

  const eventsByDate = useMemo(() => {
    const { start, end } = getRange();
    const map: Record<string, any[]> = {};

    let temp = start.clone();
    while (temp.isBefore(end) || temp.isSame(end, 'day')) {
      map[temp.format('YYYY-MM-DD')] = [];
      temp = temp.add(1, 'day');
    }

    if (!isDayLoading) {
      tasks.forEach(task => {
        if (task.start_time) {
          const deadline = parseEventDate(task.start_time);
          if (deadline.isBetween(start, end, null, '[]')) {
            const key = deadline.format('YYYY-MM-DD');
            map[key].push(task);
          }
        }
      });
    }

    return map;
  }, [tasks, getRange, isDayLoading]);

  const daysArray = useMemo(() => {
    if (calendarViewMode === 'month' || calendarViewMode === 'week' || calendarViewMode === 'day') {
      const { start, end } = getRange();
      const days: {
        date: string;
        isCurrentMonth?: boolean;
        isToday?: boolean;
        isSelected?: boolean;
        events: any[];
      }[] = [];

      let temp = start.clone();
      while (temp.isBefore(end) || temp.isSame(end, 'day')) {
        const dStr = temp.format('YYYY-MM-DD');
        const isCurrentMonth = calendarViewMode !== 'day'
          ? (temp.month() === currentDate.month())
          : true;
        const isToday = temp.isSame(dayjs(), 'day');
        const isSelected = calendarViewMode === 'day' && temp.isSame(currentDate, 'day');

        days.push({
          date: dStr,
          isCurrentMonth,
          isToday,
          isSelected,
          events: eventsByDate[dStr] || []
        });

        temp = temp.add(1, 'day');
      }
      return days;
    } else {
      const yearStart = currentDate.startOf('year');
      const months = [];
      for (let i = 0; i < 12; i++) {
        const monthDate = yearStart.clone().month(i);
        months.push({
          date: monthDate.format('YYYY-MM-DD'),
          monthDate,
          isThisMonth: monthDate.isSame(dayjs(), 'month'),
          events: []
        });
      }
      return months;
    }
  }, [calendarViewMode, currentDate, eventsByDate, getRange]);


  const goToToday = () => onDateChange(dayjs());
  const goToNext = () => {
    if (calendarViewMode === 'month') onDateChange(currentDate.add(1, 'month'));
    else if (calendarViewMode === 'week') onDateChange(currentDate.add(1, 'week'));
    else if (calendarViewMode === 'day') onDateChange(currentDate.add(1, 'day'));
    else onDateChange(currentDate.add(1, 'year'));
  };
  const goToPrev = () => {
    if (calendarViewMode === 'month') onDateChange(currentDate.subtract(1, 'month'));
    else if (calendarViewMode === 'week') onDateChange(currentDate.subtract(1, 'week'));
    else if (calendarViewMode === 'day') onDateChange(currentDate.subtract(1, 'day'));
    else onDateChange(currentDate.subtract(1, 'year'));
  };

  const handleDayClick = (day: string) => {
    if (calendarViewMode === 'month' || calendarViewMode === 'week') {
      onDateChange(dayjs(day));
      onCalendarViewModeChange('day');
    }
  };

  const handleMonthClick = (monthDate: dayjs.Dayjs) => {
    if (calendarViewMode === 'year') {
      onDateChange(monthDate);
      onCalendarViewModeChange('month');
    }
  };

  let title = '';
  if (calendarViewMode === 'month') {
    title = currentDate.format('MMMM YYYY');
  } else if (calendarViewMode === 'week') {
    const start = currentDate.startOf('week').format('MMM D');
    const end = currentDate.endOf('week').format('MMM D, YYYY');
    title = `${start} - ${end}`;
  } else if (calendarViewMode === 'day') {
    title = currentDate.format('MMMM D, YYYY');
  } else {
    title = currentDate.format('YYYY');
  }

  const selectedDay =
    calendarViewMode === 'day' || calendarViewMode === 'month' || calendarViewMode === 'week'
      ? (daysArray as any[]).find((d) => d.isSelected)
      : null;

  useEffect(() => {
    if ((calendarViewMode === 'day' || calendarViewMode === 'week') && containerRef.current && containerNavRef.current && containerOffsetRef.current) {
      const currentMinute = new Date().getHours() * 60;
      const scrollHeight =
        containerRef.current.scrollHeight -
        containerNavRef.current.offsetHeight -
        containerOffsetRef.current.offsetHeight;

      containerRef.current.scrollTop = (scrollHeight * currentMinute) / 1440;
    }
  }, [calendarViewMode]);

  /* Written out rather than interpolated: `grid-rows-${n}` is invisible to
     Tailwind's content scanner, so the old template literal emitted a class
     that was never generated and the month grid fell back to auto rows. */
  const monthRowsClass =
    calendarViewMode === 'month' && (daysArray as any[]).length === 35
      ? 'grid-rows-5'
      : 'grid-rows-6';

  const handleEventDragEnd = (eventId: string, newDateTime: Date) => {
    if (onEventDrop) onEventDrop(eventId, newDateTime);
  };

  const eventPositionForTime = (deadline: dayjs.Dayjs) => {
    const hour = deadline.hour();
    const minute = deadline.minute();
    const startRow = (hour * 12) + Math.floor(minute / 5) + 2;
    const spanRows = 6;
    return { startRow, spanRows };
  };
  const VIEW_MODES: Array<{ value: 'day' | 'week' | 'month' | 'year'; label: string }> = [
    { value: 'day', label: t('Day') },
    { value: 'week', label: t('Week') },
    { value: 'month', label: t('Month') },
    { value: 'year', label: t('Year') },
  ];

  // Current day events for day view sidebar
  const currentDayEvents = useMemo(() => {
    const key = currentDate.format('YYYY-MM-DD');
    return eventsByDate[key] || [];
  }, [currentDate, eventsByDate]);

  /** Placeholder chips laid on the time grid while a range is in flight. */
  const renderTimeGridSkeleton = (days: any[]) =>
    days.map((day, dayIndex) => (
      <React.Fragment key={day.date}>
        {[...Array(2)].map((_, i) => (
          <li
            key={i}
            className="relative mt-px flex"
            style={{ gridRow: `${1 + i * 12} / span 6`, gridColumn: `${dayIndex + 1} / span 1` }}
          >
            <Skeleton
              variant="prose"
              rows={2}
              className="absolute inset-1 h-fit rounded-md border border-hairline-soft bg-surface-sunken/50 p-2"
            />
          </li>
        ))}
      </React.Fragment>
    ));

  /** One positioned event chip on a day/week time grid. */
  const renderTimeGridEvent = (event: any, dayIndex?: number) => {
    const deadline = parseEventDate(event.start_time);
    const { startRow, spanRows } = eventPositionForTime(deadline);
    const colors = getEventColors(event.event_type);

    return (
      <li
        key={event.id}
        className="relative mt-px flex overflow-visible"
        style={{
          gridRow: `${startRow} / span ${spanRows}`,
          ...(dayIndex !== undefined ? { gridColumn: `${dayIndex + 1} / span 1` } : {}),
        }}
      >
        <button
          onClick={() => onSelectEvent(event)}
          className={classNames(
            'group absolute inset-1 flex h-fit flex-col overflow-hidden p-2 text-xs text-left',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40',
            EVENT_CHIP,
            colors.chip,
            colors.ring
          )}
        >
          <p className={`font-semibold truncate max-w-full ${colors.text}`}>
            {event.title}
          </p>
          <p className="mt-0.5 text-fg-muted">
            <time dateTime={event.start_time}>
              {deadline.format('h:mm A')}
            </time>
          </p>
        </button>
      </li>
    );
  };

  const renderDayEvents = (days: any[]) => {
    if (isDayLoading) return renderTimeGridSkeleton(days);
    return days.map((day) => day.events.map((event: any) => renderTimeGridEvent(event)));
  };

  return (
    /* The page owns its own height: `h-full min-h-0` inside the app shell
       rather than the old `h-screen`, which ignored the shell's chrome and
       pushed the grid's bottom row off the viewport. */
    <div className="flex h-full min-h-0 flex-col text-fg-body">
      <Header />

      {/* ── ONE control row (cal-1) ──────────────────────────────────────
          left: where you are and how to move · centre: which view ·
          right: the key, the filters, the primary action, and everything
          occasional folded into a single overflow menu. */}
      <div
        data-tour-id="calendar-header"
        className="glass-bar flex flex-wrap items-center gap-2 border-b border-hairline px-4 py-2"
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={goToPrev}
            title={t('Previous')}
            aria-label={t('Previous')}
            className="rounded-md p-1.5 text-fg-muted transition-colors hover:bg-surface-sunken/70 hover:text-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={goToNext}
            title={t('Next')}
            aria-label={t('Next')}
            className="rounded-md p-1.5 text-fg-muted transition-colors hover:bg-surface-sunken/70 hover:text-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
          <Button variant="outline" size="small" onClick={goToToday}>
            {t('Today')}
          </Button>
        </div>

        <h2 className="ml-1 truncate text-sm font-semibold text-fg-strong">{title}</h2>

        {/* The view switcher, as segmented pills rather than a hidden dropdown. */}
        <div className="ml-auto flex items-center gap-1" role="group" aria-label={t('View')}>
          {VIEW_MODES.map((mode) => (
            <button
              key={mode.value}
              type="button"
              onClick={() => onCalendarViewModeChange(mode.value)}
              aria-pressed={calendarViewMode === mode.value}
              className={classNames(
                'nav-pill px-2.5 py-1 text-xs',
                calendarViewMode === mode.value && 'nav-pill-active'
              )}
            >
              {mode.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5">
          {legend && (
            <Popover>
              <Popover.Button
                as="button"
                type="button"
                title={t('Event key')}
                aria-label={t('Event key')}
                className="rounded-md p-1.5 text-fg-muted transition-colors hover:bg-surface-sunken/70 hover:text-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40"
              >
                <PiPaletteLight className="h-4 w-4" />
              </Popover.Button>
              {/* `anchor` renders the panel in a portal on <body> and positions
                  it against the button — the control row is `.glass-bar`, whose
                  backdrop-filter opens a stacking context that would otherwise
                  trap any `absolute z-50` panel underneath the calendar canvas. */}
              <Popover.Panel
                anchor="bottom end"
                transition
                className="glass z-[300000001] [--anchor-gap:8px] w-56 rounded-lg p-3 shadow-lg origin-top-right transition duration-150 ease-pop data-[closed]:opacity-0 data-[closed]:translate-y-1"
              >
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-fg-muted">
                  {t('Event key')}
                </p>
                {legend}
              </Popover.Panel>
            </Popover>
          )}

          {filterControl && (
            <Button
              variant={filterControl.open ? 'neutral' : 'outline'}
              size="small"
              onClick={filterControl.onToggle}
              icon={<PiFunnelLight className="h-4 w-4" />}
            >
              {t('Filters')}
              {filterControl.activeCount > 0 && (
                <span className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-champagne px-1 text-[10px] font-semibold text-ink">
                  {filterControl.activeCount}
                </span>
              )}
            </Button>
          )}

          {onAddTask && (
            <Button
              variant="primary"
              size="small"
              onClick={onAddTask}
              icon={<PiPlusLight className="h-4 w-4" />}
            >
              {t('Add Event')}
            </Button>
          )}

          {(overflowActions?.length || 0) > 0 && (
            <Menu as="div" className="inline-block text-left">
              <Menu.Button
                as="button"
                type="button"
                title={t('More')}
                aria-label={t('More')}
                className="rounded-md p-1.5 text-fg-muted transition-colors hover:bg-surface-sunken/70 hover:text-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40"
              >
                <PiDotsThreeVerticalLight className="h-4 w-4" />
              </Menu.Button>
              {/* Same portal treatment as the legend popover above: without
                  `anchor` the menu paints inside the `.glass-bar` stacking
                  context and disappears behind the calendar grid below it. */}
              <Menu.Items
                anchor="bottom end"
                transition
                className="glass z-[300000001] [--anchor-gap:8px] w-52 rounded-lg p-1 shadow-lg focus:outline-none origin-top-right transition duration-150 ease-pop data-[closed]:opacity-0 data-[closed]:translate-y-1"
              >
                {overflowActions!.map((action, index) => (
                  <Menu.Item key={index}>
                    {({ active }: { active: boolean }) => (
                      <button
                        type="button"
                        onClick={action.onClick}
                        disabled={action.disabled}
                        title={action.title}
                        className={classNames(
                          'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-fg-body',
                          active && 'bg-surface-sunken/70 text-fg-strong',
                          action.disabled && 'cursor-not-allowed opacity-50'
                        )}
                      >
                        {action.icon}
                        {t(action.label)}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Menu>
          )}
        </div>
      </div>

      {filterBar}

      {/* The canvas: the grid is one glass pane floating on the page. */}
      <div className="relative flex min-h-0 flex-1 flex-col p-4 animate-fade-in-up">
        <div data-tour-id="calendar-content" className="absolute top-0 left-0 right-0 h-[120px] pointer-events-none z-10" />
        <GlassCard padding="none" fill className="overflow-hidden" bodyClassName="min-h-0">
        {calendarViewMode === 'year' && (
          <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-2 gap-3 overflow-auto custom-scrollbar-main p-4 sm:grid-cols-3 lg:grid-cols-4">
            {(daysArray as any[]).map(m => {
              const monthDate = m.monthDate;
              return (
                <button
                  key={m.date}
                  type="button"
                  onClick={() => handleMonthClick(monthDate)}
                  className={classNames(
                    'rounded-lg border border-hairline bg-surface-soft/60 p-4 text-center text-sm font-medium text-fg-body',
                    'transition-colors duration-150 ease-pop hover:bg-surface-sunken/70 hover:text-fg-strong',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40',
                    m.isThisMonth && 'ring-1 ring-inset ring-champagne text-fg-strong'
                  )}
                >
                  {monthDate.format('MMMM YYYY')}
                </button>
              );
            })}
          </div>
        )}

        {calendarViewMode === 'month' && (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="glass-bar grid flex-none grid-cols-7 border-b border-hairline text-center text-[11px] font-medium uppercase tracking-wide text-fg-muted">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                <div key={i} className="py-2">{d}</div>
              ))}
            </div>
            <div className="min-h-0 flex-1 overflow-auto custom-scrollbar-main text-xs text-fg-body">
              <div className={classNames('grid h-full w-full grid-cols-7', monthRowsClass)}>
                {(daysArray as any[]).map(day => {
                  const d = dayjs(day.date);
                  return (
                    <div
                      key={day.date}
                      onClick={() => handleDayClick(day.date)}
                      className={classNames(
                        'relative min-h-[5rem] cursor-pointer border-b border-r border-hairline-soft px-3 py-2',
                        'transition-colors duration-150 hover:bg-surface-sunken/60',
                        day.isCurrentMonth ? 'text-fg-body' : 'bg-surface-sunken/40 text-fg-muted'
                      )}
                    >
                      <time
                        dateTime={day.date}
                        className={classNames(
                          'flex h-6 w-6 items-center justify-center rounded-full text-xs',
                          day.isToday && 'bg-champagne font-semibold text-ink'
                        )}
                      >
                        {d.date()}
                      </time>
                      {isDayLoading ? (
                        <Skeleton variant="prose" rows={2} className="mt-2" />
                      ) : day.events.length > 0 && (
                        <ol className="mt-2 space-y-1">
                          {day.events.slice(0, 2).map((event: any) => (
                            <EventItem key={event.id} event={event} onSelect={onSelectEvent} />
                          ))}
                          {day.events.length > 2 && (
                            <li className="pt-1 text-xs text-fg-muted">
                              + {day.events.length - 2} more
                            </li>
                          )}
                        </ol>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {selectedDay && selectedDay.events.length > 0 && (
              <div className="flex-none border-t border-hairline px-4 py-4 sm:px-6 lg:hidden">
                <ol className="divide-y divide-hairline-soft overflow-hidden rounded-lg border border-hairline bg-surface-sunken/70 text-sm">
                  {selectedDay.events.map((event: any) => (
                    <li key={event.id} className="group flex p-4 pr-6 transition-colors hover:bg-surface-sunken">
                      <div className="flex-auto">
                        <p className="font-semibold text-fg-strong">
                          {event.title}
                        </p>
                        {event.start_time && (
                          <time dateTime={event.start_time} className="mt-2 flex items-center text-fg-muted">
                            <ClockIcon className="mr-2 h-5 w-5 text-fg-muted" aria-hidden="true" />
                            {parseEventDate(event.start_time).format('h:mm A')}
                          </time>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="small"
                        onClick={() => onSelectEvent(event)}
                        className="ml-6 flex-none self-center"
                      >
                        {t('Open')}<span className="sr-only">, {event.title}</span>
                      </Button>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {calendarViewMode === 'week' && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div
              ref={containerRef}
              className="isolate relative flex flex-auto flex-col overflow-auto custom-scrollbar-main"
            >
              <div
                ref={containerNavRef}
                className="glass-bar sticky top-0 z-30 flex-none border-b border-hairline sm:pr-8"
              >
                <div className="grid grid-cols-7 text-sm text-fg-muted sm:hidden">
                  {(daysArray as any[]).map((day) => {
                    const d = dayjs(day.date);
                    return (
                      <button key={day.date} type="button" className="flex flex-col items-center border-r border-hairline-soft pb-3 pt-2">
                        {d.format('dd').charAt(0)} <span className={classNames(
                          "mt-1 flex size-8 items-center justify-center font-semibold",
                          day.isToday ? 'rounded-full bg-champagne text-ink' : 'text-fg-strong'
                        )}>{d.date()}</span>
                      </button>
                    )
                  })}
                </div>
                <div className="-mr-px hidden grid-cols-7 divide-x divide-hairline-soft border-r border-hairline-soft text-sm text-fg-muted sm:grid">
                  <div className="col-end-1 w-14" />
                  {(daysArray as any[]).map((day) => {
                    const d = dayjs(day.date);
                    return (
                      <div key={day.date} className="flex items-center justify-center py-3">
                        <span className="flex items-baseline">
                          <span>{d.format('ddd')}</span> <span className={classNames(
                            "ml-1.5 items-center justify-center font-semibold",
                            day.isToday ? 'rounded-full bg-champagne px-2 text-ink' : 'text-fg-strong'
                          )}>{d.date()}</span>
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="flex h-full flex-auto overflow-auto">
                <div
                  className="sticky left-0 z-10 w-14 flex-none border-r border-hairline-soft"
                />
                <div className="grid flex-auto grid-cols-7 grid-rows-1">
                  <div
                    className="col-start-1 col-end-8 row-start-1 grid divide-y divide-hairline-soft"
                    style={{ gridTemplateRows: 'repeat(48, minmax(3.5rem, 1fr))' }}
                  >
                    <div ref={containerOffsetRef} className="row-end-1 h-7"></div>
                    {[...Array(24)].map((_, hour) => (
                      <React.Fragment key={hour}>
                        <div>
                          <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs text-fg-muted">
                            {hour === 0 ? '12AM' : hour < 12 ? `${hour}AM` : hour === 12 ? '12PM' : `${hour - 12}PM`}
                          </div>
                        </div>
                        <div className="border-b border-hairline-soft" />
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="col-start-1 col-end-8 row-start-1 grid grid-cols-7 divide-x divide-hairline-soft">
                    <div className="col-start-1 row-span-full border-r border-hairline-soft" />
                    <div className="col-start-2 row-span-full border-r border-hairline-soft" />
                    <div className="col-start-3 row-span-full border-r border-hairline-soft" />
                    <div className="col-start-4 row-span-full border-r border-hairline-soft" />
                    <div className="col-start-5 row-span-full border-r border-hairline-soft" />
                    <div className="col-start-6 row-span-full border-r border-hairline-soft" />
                    <div className="col-start-7 row-span-full border-r border-hairline-soft" />
                  </div>
                  <ol
                    className="col-start-1 col-end-8 row-start-1 grid grid-cols-7 pointer-events-auto sm:pr-8"
                    style={{ gridTemplateRows: '1.75rem repeat(288, minmax(0,1fr)) auto' }}
                  >
                    {isDayLoading && renderTimeGridSkeleton(daysArray as any[])}
                    {!isDayLoading && (daysArray as any[]).map((day, dayIndex) => (
                      <React.Fragment key={day.date}>
                        {day.events.map((event: any) => renderTimeGridEvent(event, dayIndex))}
                      </React.Fragment>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}
        {calendarViewMode === 'day' && (
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <div
              ref={containerRef}
              className="relative flex flex-auto flex-col overflow-auto custom-scrollbar-main"
            >
              <div
                ref={containerNavRef}
                className="glass-bar sticky top-0 z-10 grid flex-none grid-cols-7 border-b border-hairline text-xs text-fg-muted md:hidden"
              />
              <div className="flex w-full flex-auto">
                <div className="w-14 flex-none border-r border-hairline-soft" />
                <div className="grid flex-auto grid-cols-1 grid-rows-1 relative">
                  <div
                    className="col-start-1 col-end-2 row-start-1 grid divide-y divide-hairline-soft"
                    style={{ gridTemplateRows: 'repeat(48, minmax(3.5rem, 1fr))' }}
                  >
                    <div ref={containerOffsetRef} className="row-end-1 h-7"></div>
                    {[...Array(24)].map((_, hour) => (
                      <React.Fragment key={hour}>
                        <div>
                          <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs text-fg-muted">
                            {hour === 0
                              ? '12AM'
                              : hour < 12
                                ? `${hour}AM`
                                : hour === 12
                                  ? '12PM'
                                  : `${hour - 12}PM`}
                          </div>
                        </div>
                        <div />
                      </React.Fragment>
                    ))}
                  </div>

                  <ol
                    className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 pointer-events-auto"
                    style={{ gridTemplateRows: '1.75rem repeat(288, minmax(0,1fr)) auto' }}
                  >
                    {renderDayEvents(daysArray as any[])}
                  </ol>
                </div>
              </div>
            </div>
            {/* The day rail: mini-month + the day's agenda. Owns its own
                scrolling so the time grid beside it keeps its height. */}
            <div className="hidden w-1/2 max-w-md flex-none overflow-y-auto custom-scrollbar-main border-l border-hairline px-6 py-6 md:block">
              <div className="mb-4 flex items-center text-center text-fg-strong">
                <button
                  type="button"
                  className="-m-1.5 flex flex-none items-center justify-center rounded-md p-1.5 text-fg-muted transition-colors hover:bg-surface-sunken/70 hover:text-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40"
                  onClick={() => onDateChange(currentDate.subtract(1, 'month'))}
                >
                  <span className="sr-only">{t("Previous month")}</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                <div className="flex-auto text-sm font-semibold">{currentDate.format('MMMM YYYY')}</div>
                <button
                  type="button"
                  className="-m-1.5 flex flex-none items-center justify-center rounded-md p-1.5 text-fg-muted transition-colors hover:bg-surface-sunken/70 hover:text-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40"
                  onClick={() => onDateChange(currentDate.add(1, 'month'))}
                >
                  <span className="sr-only">{t("Next month")}</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <div className="mb-2 grid grid-cols-7 text-center text-[11px] uppercase tracking-wide text-fg-muted">
                <div>M</div><div>T</div><div>W</div><div>T</div><div>F</div><div>S</div><div>S</div>
              </div>
              <div className="isolate grid grid-cols-7 overflow-hidden rounded-lg border border-hairline bg-surface-sunken/40 text-sm">
                {[...Array(currentDate.daysInMonth())].map((_, i) => {
                  const d = currentDate.startOf('month').add(i, 'day');
                  const isToday = d.isSame(dayjs(), 'day');
                  const isSelected = d.isSame(currentDate, 'day');
                  const dayKey = d.format('YYYY-MM-DD');
                  const hasEvents = (eventsByDate[dayKey] || []).length > 0;
                  return (
                    <button
                      key={d.format('YYYY-MM-DD')}
                      type="button"
                      onClick={() => {
                        onDateChange(d);
                        onCalendarViewModeChange('day');
                      }}
                      className={classNames(
                        'py-1.5 transition-colors hover:bg-surface-sunken focus:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40',
                        isToday && !isSelected && 'font-semibold text-champagne'
                      )}
                    >
                      <time
                        dateTime={d.format('YYYY-MM-DD')}
                        className={classNames(
                          'mx-auto flex size-7 items-center justify-center rounded-full',
                          isSelected ? 'bg-champagne font-semibold text-ink' : 'text-fg-body'
                        )}
                      >
                        {d.date()}
                      </time>
                      {hasEvents && (
                        <span className="mx-auto mt-0.5 block h-1 w-1 rounded-full bg-champagne" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Day agenda */}
              <div className="mt-6">
                <h3 className="mb-3 text-[11px] font-medium uppercase tracking-wide text-fg-muted">
                  {currentDate.format('dddd, MMMM D')}
                </h3>
                {isDayLoading ? (
                  <Skeleton variant="option" rows={3} />
                ) : currentDayEvents.length > 0 ? (
                  <ol className="space-y-2">
                    {currentDayEvents.map((event: any) => {
                      const colors = getEventColors(event.event_type);
                      return (
                        <li key={event.id}>
                          <button
                            onClick={() => onSelectEvent(event)}
                            className={classNames(
                              'w-full px-3 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40',
                              EVENT_CHIP,
                              colors.chip,
                              colors.ring
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${colors.dot}`} />
                              <p className={`truncate text-sm font-medium ${colors.text}`}>
                                {event.title}
                              </p>
                            </div>
                            {event.start_time && (
                              <p className="ml-4 mt-1 text-xs text-fg-muted">
                                {parseEventDate(event.start_time).format('h:mm A')}
                                {event.end_time && ` – ${parseEventDate(event.end_time).format('h:mm A')}`}
                              </p>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <EmptyState
                    density="compact"
                    icon={<PiCalendarBlankLight />}
                    headline={t('Nothing scheduled')}
                    subtext={t('There are no events on {{date}}. Pick another day from the calendar above.', {
                      date: currentDate.format('dddd, MMMM D'),
                    })}
                    {...(onAddTask ? { button: { label: t('Add Event'), onClick: onAddTask } } : {})}
                  />
                )}
              </div>
            </div>
          </div>
        )}
        </GlassCard>
      </div>
    </div>
  );
};

export default CalendarView;
